import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { getExecutionRunById } from '@/features/executionRuns/data/executionRuns';
import { TableV2NestedEmptyState } from '@/features/shared/components/tableSystem/TableV2NestedEmptyState';
import { TableV2NestedFrame } from '@/features/shared/components/tableSystem/TableV2NestedFrame';
import {
  resolveTableV2NestedDividerColor,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2NestedCollapse,
  TableV2SortableHeader,
  TableV2SkeletonRow,
  tableV2ChevronColumnWidth,
  tableV2NestedHeaderHeight,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { queryKeys } from '@/shared/hooks';
import { s, typographyTokens } from '@/theme/tokens';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  type SxProps,
  type Theme,
} from '@/ui/primitives';
import { SuiteRunDetailRow } from './ExecutionRunsTableV2NestedDetailRows';
import {
  defaultNestedSuiteRunSort,
  getSuiteKeyForTest,
  getSummaryName,
  sortSuiteRunSummaries,
  type SortableNestedSuiteRunField,
} from './ExecutionRunsTableV2NestedSorting';
import { countSuites, nestedDetailContentSx } from './ExecutionRunsTableV2RowHelpers';
import type { TagColor } from '@/shared/data/tags';
import type { ExecutionRunDto, SuiteRunDto, TestRunDto, TestRunSummaryDto } from '@flowqa/shared';

const expandedRunSkeletonColumns = [
  'chevron',
  'name',
  'status',
  'passRate',
  'dateTime',
  'duration',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

const suiteRunNestedChevronColumnWidth = tableV2ChevronColumnWidth;
const nestedSuiteTableSurfaceSx = tableV2NestedDepthSx[1].table satisfies SxProps<Theme>;

export const ExpandedRunDetails = ({
  expanded,
  groupErroredWithFailed,
  organisationId,
  row,
  tagColorByName,
  suiteRunDeleteRowPendingId,
  suiteRunEditRowPendingId,
  testRunDeleteRowPendingId,
  testRunEditRowPendingId,
  onDeleteSuiteRun,
  onDeleteTestRun,
  onEditSuiteRun,
  onEditTestRun,
}: {
  expanded: boolean;
  groupErroredWithFailed: boolean;
  organisationId: string | null;
  row: ExecutionRunDto;
  tagColorByName: Partial<Record<string, TagColor>>;
  suiteRunDeleteRowPendingId: string | null;
  suiteRunEditRowPendingId: string | null;
  testRunDeleteRowPendingId: string | null;
  testRunEditRowPendingId: string | null;
  onDeleteSuiteRun: (row: SuiteRunDto) => void;
  onDeleteTestRun: (row: TestRunDto) => void;
  onEditSuiteRun: (row: SuiteRunDto) => void;
  onEditTestRun: (row: TestRunDto) => void;
}): ReactElement => {
  const [suiteRunSort, setSuiteRunSort] = useState<{
    field: SortableNestedSuiteRunField;
    direction: 'asc' | 'desc';
  }>(defaultNestedSuiteRunSort);
  const detailQuery = useQuery({
    queryKey: queryKeys.executionRunDetail(row.id, organisationId),
    enabled: expanded,
    staleTime: 30_000,
    queryFn: async () => await getExecutionRunById(row.id, organisationId),
  });
  const detail = detailQuery.data ?? row;
  const suites = detail.suiteRunSummaries ?? [];
  const tests = detail.testRunSummaries ?? [];
  const loadedSuiteSummaryCount = row.suiteRunSummaries?.length ?? 0;
  const suiteSkeletonRowCount = resolveTableV2NestedSkeletonRowCount(
    loadedSuiteSummaryCount > 0 ? loadedSuiteSummaryCount : countSuites(row).total,
  );
  const testsBySuite = new Map<string, TestRunSummaryDto[]>();
  tests.forEach((testSummary) => {
    const key = getSuiteKeyForTest(testSummary);
    const suiteTests = testsBySuite.get(key) ?? [];
    suiteTests.push(testSummary);
    testsBySuite.set(key, suiteTests);
  });
  const displayedSuites = sortSuiteRunSummaries(suites, suiteRunSort, testsBySuite);

  const handleSuiteRunSortChange = (field: SortableNestedSuiteRunField): void => {
    setSuiteRunSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const suiteRunHeader = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: suiteRunNestedChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={suiteRunSort}
          field="runNumber"
          label="Suite Run"
          onSortChange={handleSuiteRunSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={suiteRunSort}
          field="status"
          label="Status"
          onSortChange={handleSuiteRunSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={suiteRunSort}
          field="tests"
          label="Pass Rate"
          onSortChange={handleSuiteRunSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={suiteRunSort}
          field="durationMs"
          label="Duration"
          onSortChange={handleSuiteRunSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={suiteRunSort}
          field="timestamp"
          label="Ran At"
          onSortChange={handleSuiteRunSortChange}
        />
      </TableCell>
      <TableCell>Tags</TableCell>
      <TableCell align="center" sx={{ height: tableV2NestedHeaderHeight, p: s('none') }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    </TableRow>
  );
  const suiteRunColgroup = (
    <colgroup>
      <col width={suiteRunNestedChevronColumnWidth} />
      <col width="20%" />
      <col width="9%" />
      <col width="16%" />
      <col width="11%" />
      <col width="20%" />
      <col width="20%" />
      <col width="4%" />
    </colgroup>
  );

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={nestedDetailContentSx}>
        {detailQuery.isLoading ? (
          <TableV2NestedFrame depth={1}>
            <Table
              aria-label="Suite runs loading"
              sx={[
                nestedSuiteTableSurfaceSx,
                {
                  tableLayout: 'fixed',
                  '& th': {
                    borderBottom: (theme) => `1px solid ${resolveTableV2NestedDividerColor(theme)}`,
                    color: 'text.primary',
                    fontSize: typographyTokens.body.fontSize,
                    fontWeight: typographyTokens.body.fontWeight,
                    height: tableV2NestedHeaderHeight,
                    py: s('none'),
                    verticalAlign: 'middle',
                  },
                  '& td': {
                    fontSize: typographyTokens.body.fontSize,
                  },
                  '& .MuiTypography-root': {
                    fontSize: typographyTokens.body.fontSize,
                  },
                },
              ]}
            >
              {suiteRunColgroup}
              <TableHead>{suiteRunHeader}</TableHead>
              <TableBody>
                {Array.from({ length: suiteSkeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`execution-run-detail-skeleton-${String(index)}`}
                    columns={expandedRunSkeletonColumns}
                    isLastVisibleRow={index === suiteSkeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : suites.length > 0 ? (
          <TableV2NestedFrame depth={1}>
            <Table
              aria-label="Suite runs"
              sx={[
                nestedSuiteTableSurfaceSx,
                {
                  tableLayout: 'fixed',
                  '& th': {
                    borderBottom: (theme) => `1px solid ${resolveTableV2NestedDividerColor(theme)}`,
                    color: 'text.primary',
                    fontSize: typographyTokens.body.fontSize,
                    fontWeight: typographyTokens.body.fontWeight,
                    height: tableV2NestedHeaderHeight,
                    py: s('none'),
                    verticalAlign: 'middle',
                  },
                  '& tbody tr:last-of-type > td': {
                    borderBottom: 0,
                  },
                  '& td': {
                    fontSize: typographyTokens.body.fontSize,
                  },
                  '& .MuiTypography-root': {
                    fontSize: typographyTokens.body.fontSize,
                  },
                },
              ]}
            >
              {suiteRunColgroup}
              <TableHead>{suiteRunHeader}</TableHead>
              <TableBody>
                {displayedSuites.map((suite, index) => (
                  <SuiteRunDetailRow
                    key={`${suite.suiteId}:${suite.executionId ?? ''}`}
                    deleteRowPendingId={suiteRunDeleteRowPendingId}
                    editRowPendingId={suiteRunEditRowPendingId}
                    groupErroredWithFailed={groupErroredWithFailed}
                    isLastSuite={index === displayedSuites.length - 1}
                    parent={row}
                    tagColorByName={tagColorByName}
                    testRunDeleteRowPendingId={testRunDeleteRowPendingId}
                    testRunEditRowPendingId={testRunEditRowPendingId}
                    suite={suite}
                    tests={testsBySuite.get(getSummaryName(suite)) ?? []}
                    onDelete={onDeleteSuiteRun}
                    onDeleteTestRun={onDeleteTestRun}
                    onEdit={onEditSuiteRun}
                    onEditTestRun={onEditTestRun}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : (
          <TableV2NestedEmptyState
            ariaLabel="Suite runs"
            colgroup={suiteRunColgroup}
            colSpan={8}
            depth={1}
            header={suiteRunHeader}
          />
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};
